import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Popover, { PopoverProps } from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import { alpha, useTheme } from '@mui/material/styles'
import { SxProps } from '@mui/system'
import tinycolor from 'tinycolor2'
import clsx from 'clsx'
import moment from 'moment-timezone'
import { DateRange, RangeKeyDict, RangeFocus, CalendarProps } from 'react-date-range'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import useId from '@mui/utils/useId'
import useStateRef from 'react-usestateref'

import {
  convertToAbsoluteTimeRange,
  isSameTimeRange,
  isValidTimeRange,
  isStartOfDayEndOfDay,
  dateRangeToTimeRange,
  generatePredefinedTimeRanges,
  valueToDateRange,
  momentToLocalDate
} from './dateRangeUtils'

import TimeSelector from './TimeSelector'
import { useDateRangeStyles } from './useDateRangeStyles'
import { MonthAndYearSelector, MonthAndYearSelectorProps } from './MonthAndYearSelector'
import { useTimezone } from 'lib/TimezoneProvider'
import { PartialTimeRange, RelativeTimeRangeLabel, TimeRange } from './types'
import { DateField } from '@mui/x-date-pickers/DateField'
import ToggleButton from 'components/UI/ToggleButton/ToggleButton'
import { SelectInputProps } from '@mui/material/Select/SelectInput'


type PredefinedValuesProps = {
  sx?: SxProps
  currentValue: PartialTimeRange
  onChange: SelectInputProps<RelativeTimeRangeLabel>['onChange'];
}

function PredefinedValues({ sx, currentValue, onChange }: PredefinedValuesProps) {
  const uniqueId = useId()
  const predefinedValues = useMemo(() => generatePredefinedTimeRanges(), [])

  return (
    <FormControl sx={sx}>
      <InputLabel id={`date-range-label_${uniqueId}`}>Date Range</InputLabel>
      <Select
        labelId={`date-range-label_${uniqueId}`}
        value={currentValue.label || 'Custom'}
        label="Date Range"
        onChange={onChange}
        data-testid="predefinedRange"
      >
        {predefinedValues.map((pre, index) => (
          <MenuItem key={index} value={pre.label}>
            {pre.label}
          </MenuItem>
        ))}
        <MenuItem disabled value="Custom">
          Custom
        </MenuItem>
      </Select>
    </FormControl>
  )
}

export type DatePickerPopoverProps = {
  initialValue: PartialTimeRange
  onChange?: (value: TimeRange) => void
  onClose?: () => void
  timeInputsEnabled?: boolean
  timezone?: string
  lockedStart?: boolean
  showPredefinedValues?: boolean
  isSingleMonth?: boolean
} & Omit<PopoverProps, 'value' | 'onChange' | 'onClose'>

export default function DatePickerPopover({
  lockedStart = false,
  showPredefinedValues = true,
  isSingleMonth = false,
  initialValue,
  onChange = () => undefined,
  onClose = () => undefined,
  timeInputsEnabled = false,
  timezone: timezoneFromProps,
  ...popoverProps
}: DatePickerPopoverProps) {
  // Disable predefined values if lockedStart is true
  if(lockedStart) {
    showPredefinedValues = false
    isSingleMonth = true
  }

  const classes = useDateRangeStyles({ isSingleMonth })
  const theme = useTheme()
  const { timezone: timezoneFromContext } = useTimezone()
  const timezone = timezoneFromProps ?? timezoneFromContext

  const rangeColors = useMemo(
    () => [tinycolor(theme.palette.primary.main).setAlpha(0.1).toRgbString()],
    [theme.palette.primary.main]
  )

  const [currentValue, setCurrentValue, valueRef] = useStateRef<PartialTimeRange>(initialValue)
  const currentAbsoluteValue = useMemo(
    () => convertToAbsoluteTimeRange(currentValue, timezone),
    [currentValue, timezone]
  )

  const range = valueToDateRange(currentAbsoluteValue)

  const maxDate = useMemo(() => momentToLocalDate(moment.tz(timezone)), [timezone])
  const minDate = useMemo(() => lockedStart ? range.startDate ?? maxDate : undefined, [lockedStart, maxDate, range.startDate])

  const [showTime, setShowTime] = useState(() =>
    Boolean(
      timeInputsEnabled &&
      initialValue.timeRange &&
      !isStartOfDayEndOfDay(initialValue.timeRange)
    )
  )

  // Sync if prop changes
  useEffect(() => {
    if (valueRef.current !== initialValue) {
      setCurrentValue(initialValue)
      setShowTime(
        Boolean(
          timeInputsEnabled &&
          initialValue.timeRange &&
          !isStartOfDayEndOfDay(initialValue.timeRange)
        )
      )
    }
  }, [initialValue, setCurrentValue, timeInputsEnabled, valueRef])

  useEffect(() => {
    if (!currentAbsoluteValue || !timeInputsEnabled) {
      setShowTime(false)
    }
  }, [currentAbsoluteValue, timeInputsEnabled])

  const handleClose = useCallback(() => {
    if (!isSameTimeRange(initialValue, valueRef.current) && isValidTimeRange(valueRef.current)) {
      onChange(valueRef.current)
    }
    onClose()
  }, [initialValue, onChange, onClose, valueRef])

  const handleSelect = useCallback(
    ({ selection }: RangeKeyDict) => {
      setCurrentValue(prev =>
        dateRangeToTimeRange(selection, convertToAbsoluteTimeRange(prev, timezone), timezone)
      )
    },
    [setCurrentValue, timezone]
  )

  const handleRangeFocusChange = useCallback(
    (focusRange: RangeFocus) => {
      if (focusRange[0] === 0 && focusRange[1] === 0) {
        handleClose()
      }
    },
    [handleClose]
  )

  const handleSelectPredefined = useCallback(
    (event: SelectChangeEvent) => {
      const newValue = { label: event.target.value as RelativeTimeRangeLabel }
      setCurrentValue(newValue)
      handleClose()
    },
    [handleClose, setCurrentValue]
  )

  const renderMonthAndYear = useCallback(
    (focusedDate: Date, changeShownDate: MonthAndYearSelectorProps['changeShownDate'], props: CalendarProps) => (
      <MonthAndYearSelector
        focusedDate={focusedDate}
        changeShownDate={changeShownDate}
        isSingleMonth={isSingleMonth}
        canChangeMonthYear={!isSingleMonth}
        {...props}
      />
    ),
    [isSingleMonth]
  )

  const isEmpty = currentAbsoluteValue === null

  return (
    <Popover
      {...popoverProps}
      onClose={handleClose}
      role="dialog"
      aria-label="Select Date Range"
    >
      <div className={classes.root}>
        <div className={classes.title}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            Select Date Range
            {timeInputsEnabled && (
              <ToggleButton
                disabled={!currentAbsoluteValue}
                onClick={() => setShowTime(prev => !prev)}
                icon={<AccessTimeIcon />}
                toggleOn={showTime}
                label="Toggle Time Fields"
              />
            )}
          </Stack>
        </div>

        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={2} sx={{ mx: 4 }}>
            {showPredefinedValues &&  (
              <PredefinedValues
                currentValue={currentValue}
                onChange={handleSelectPredefined}
                sx={{ flex: '2' }}
              />
            )}

            <DateField
              value={currentAbsoluteValue?.[0]}
              label="Date"
              inputProps={{ 'aria-label': 'Start Date', size: 1 }}
              disabled={lockedStart}
              onChange={(value) =>
                setCurrentValue((prev) => ({
                  timeRange: [
                    value,
                    prev.timeRange?.[1] ?? currentAbsoluteValue?.[1] ?? value,
                  ],
                }))
              }
              sx={{ flex: '1' }}
            />
            <DateField
              value={currentAbsoluteValue?.[1]}
              label="Date"
              inputProps={{ 'aria-label': 'End Date', size: 1 }}
              onChange={(value) =>
                setCurrentValue((prev) => ({
                  timeRange: [
                    prev.timeRange?.[0] ?? currentAbsoluteValue?.[0] ?? value,
                    value,
                  ],
                }))
              }
              sx={{ flex: '1' }}
            />
          </Stack>

          {showTime && currentAbsoluteValue?.[0] && currentAbsoluteValue?.[1] && (
            <TimeSelector
              currentAbsoluteValue={[currentAbsoluteValue[0], currentAbsoluteValue[1]]}
              setTimeRange={setCurrentValue}
            />
          )}
        </Stack>

        <Divider sx={{ mt: 2, mx: 2, mb: 0.5, borderColor: alpha(theme.palette.divider, 0.08) }} />

        <DateRange
          className={clsx(classes.dateRange, { [classes.emptyRange]: isEmpty })}
          dateDisplayFormat="MM/dd/yyyy"
          onChange={handleSelect}
          onRangeFocusChange={handleRangeFocusChange}
          ranges={[range]}
          moveRangeOnFirstSelection={false}
          showDateDisplay={false}
          color={theme.palette.primary.main}
          rangeColors={rangeColors}
          navigatorRenderer={renderMonthAndYear}
          minDate={minDate}
          maxDate={maxDate}
          months={isSingleMonth ? 1 : 2}
          direction="horizontal"
          calendarFocus="backwards"
          preventSnapRefocus
          focusedRange={lockedStart ? [0, 1] : undefined}
          dragSelectionEnabled={!lockedStart}
        />
      </div>
    </Popover>
  )
}
